import { AdminApiError, type AdminClient } from '@apx/admin-sdk';
import {
  spaceheroMultipliersSchema,
  type SpaceHeroMultipliersOverride,
} from '@apx/integrator-config/schemas';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { showToast } from '../toast.js';

export interface SpaceHeroMultipliersPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  /** Whether the current scope is the provider baseline (vs. a specific integrator override). */
  isProviderScope: boolean;
}

/**
 * Baked default, in bps. Mirrors `DEFAULT_RTP_BPS` in
 * packages/games/spacehero/src/flight-math.ts. Duplicated rather than imported
 * to keep the game module out of the admin bundle; if the module default
 * changes, change it here in the same commit.
 */
export const SPACEHERO_DEFAULT_RTP_BPS = 9600;
/** Mirror the bounds on `spaceheroMultipliersSchema` in @apx/integrator-config. */
export const SPACEHERO_MIN_RTP_PCT = 50;
export const SPACEHERO_MAX_RTP_PCT = 100;

/** Percent to bps. Two decimals of a percent is exactly one bps. */
export function spaceheroPctToBps(pct: number): number {
  return Math.round(pct * 100);
}

/** bps to the two-decimal percent the form shows: 9600 reads "96.00". */
export function spaceheroBpsToPct(bps: number): string {
  return (bps / 100).toFixed(2);
}

/**
 * The `multipliers` surface the form proposes for a typed percentage. An empty
 * or unreadable field becomes 0 bps, which the schema refuses, so a blank form
 * can never be proposed as a real figure.
 */
export function spaceheroProposalFromPct(pctInput: string): SpaceHeroMultipliersOverride {
  return { version: 1, rtpBps: spaceheroPctToBps(Number(pctInput) || 0) };
}

/** The constant a stored surface carries, or the baked default when there is
 *  no override yet (or it holds something that is not a number). */
export function spaceheroStoredRtpBps(value: unknown): number {
  const bps = (value as { rtpBps?: unknown } | null | undefined)?.rtpBps;
  return typeof bps === 'number' ? bps : SPACEHERO_DEFAULT_RTP_BPS;
}

function rtpColor(rtp: number): string {
  if (rtp >= 1.0) return 'var(--accent-red)';
  if (rtp > 0.97) return 'var(--accent-amber)';
  return 'var(--accent-green)';
}

/**
 * Space Hero "RTP setting" editor.
 *
 * The game has no paytable: the player names the multiplier. Its only tunable
 * is the RTP constant `k` itself (`multipliers.rtpBps`). `P(reach x) = k / x`
 * and a win returns `x`, so every target returns exactly `k`: one number, with
 * no per-bet breakdown to balance. Edited as a percentage, stored as bps.
 * Changes ride the two-admin approval flow and apply from the next round; a
 * round already drawn keeps the constant it was drawn under.
 *
 * A game with `hasMultipliers` and no editor of its own is given the generic
 * name-to-multiplier paytable editor, whose save stores rows this game never
 * reads and never reaches `rtpBps`.
 */
export async function renderSpaceHeroMultipliersPage(
  root: HTMLElement,
  deps: SpaceHeroMultipliersPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">RTP setting — Space Hero</h1>
    <p class="page-sub">Space Hero returns whatever multiplier the player names, so there is no paytable. The single setting below <strong>is</strong> the RTP: every target, 1.01× to 10,000×, returns exactly this figure. Changes go through two-admin approval and apply from the next round.</p>

    <div class="card">
      <h2>Return to player</h2>
      <table>
        <thead><tr><th>Setting</th><th>RTP (%)</th><th>House edge</th><th></th></tr></thead>
        <tbody>
          <tr>
            <td><strong>RTP constant <code>k</code></strong><br><span class="muted">Same at every target · ${SPACEHERO_MIN_RTP_PCT}–${SPACEHERO_MAX_RTP_PCT}%</span></td>
            <td><input id="spacehero-rtp" type="number" min="${SPACEHERO_MIN_RTP_PCT}" max="${SPACEHERO_MAX_RTP_PCT}" step="0.01" style="width:120px" /></td>
            <td class="muted" id="spacehero-rtp-hint">—</td>
            <td class="muted" data-default>default ${spaceheroBpsToPct(SPACEHERO_DEFAULT_RTP_BPS)}%</td>
          </tr>
        </tbody>
      </table>
      <div class="row" style="margin-top:14px">
        <button id="spacehero-reset" class="ghost">Reset to default</button>
        <button id="spacehero-save" class="primary">Propose change</button>
        <span id="spacehero-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="spacehero-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const rtpEl = root.querySelector<HTMLInputElement>('#spacehero-rtp')!;
  const hintEl = root.querySelector<HTMLElement>('#spacehero-rtp-hint')!;
  const msgEl = root.querySelector<HTMLElement>('#spacehero-msg')!;

  function updateHint(): void {
    const rtp = spaceheroPctToBps(Number(rtpEl.value) || 0) / 10000;
    hintEl.innerHTML = `<strong style="color:${rtpColor(rtp)}">${((1 - rtp) * 100).toFixed(2)}%</strong>`;
  }
  rtpEl.addEventListener('input', updateHint);

  function fillForm(value: unknown): void {
    rtpEl.value = spaceheroBpsToPct(spaceheroStoredRtpBps(value));
    updateHint();
  }

  const { value: current } = await deps.client.getSurface(
    deps.integratorId,
    deps.gameId,
    'multipliers',
  );
  fillForm(current);

  root.querySelector<HTMLButtonElement>('#spacehero-reset')!.addEventListener('click', () => {
    fillForm(null);
    msgEl.textContent = 'Reset to module default (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#spacehero-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = spaceheroMultipliersSchema.safeParse(spaceheroProposalFromPct(rtpEl.value));
    if (!parsed.success) {
      msgEl.textContent = `RTP must be ${SPACEHERO_MIN_RTP_PCT}–${SPACEHERO_MAX_RTP_PCT}%, to two decimals.`;
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'multipliers',
        parsed.data,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent =
        err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'multipliers' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#spacehero-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th>Proposed RTP</th><th></th></tr></thead>
      <tbody>${subset
        .map((a) => {
          const bps = (a.pendingConfig as { rtpBps?: unknown } | null)?.rtpBps;
          const shown = typeof bps === 'number' ? `${spaceheroBpsToPct(bps)}%` : '—';
          return `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>${shown}</td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`;
        })
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved. The new RTP applies from the next round.';
          msgEl.className = 'muted';
          await refreshPending();
          const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'multipliers');
          fillForm(fresh.value);
        } catch (err) {
          showToast(
            err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err),
            'error',
          );
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(
            err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err),
            'error',
          );
        }
      });
    });
  }

  await refreshPending();
}
